import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Button, Modal, CodeEditorField } from '@dopomogai/ui';
import { toast } from '@dopomogai/ui';

import { useCreateAgent } from '@dopomogai/agent-kit';
import type {
  CreateAgentPayload,
  QuickstartAgentResult,
} from '@dopomogai/agent-kit';
import { widgetInstallationsService, widgetService, isApiError } from '@dopomogai/api-client';
import type { UUID } from '@dopomogai/types';

import { AgentBuilderStepper } from '../components/AgentBuilderStepper';
import { DefineAgentStep } from '../components/steps/DefineAgentStep';
import { CraftMindStep } from '../components/steps/CraftMindStep';
import { AgentAbilitiesStep } from '../components/steps/AgentAbilitiesStep';
import { OnboardingAssistantPanel } from '../components/OnboardingAssistantPanel';
import type { AgentBuilderFormData } from '../types';
import {
  buildOnboardingAssistantContext,
  createInitialOnboardingAssistantFieldSyncState,
  type OnboardingAssistantFieldPatch,
  type OnboardingAssistantSyncField,
  type OnboardingAssistantSyncPatchResult,
} from '../types/onboardingAssistant';

const STEPS = [
  { id: 'define', nameKey: 'stepper.define' },
  { id: 'craft', nameKey: 'stepper.craft' },
  { id: 'abilities', nameKey: 'stepper.abilities' },
];

const initialFormData: AgentBuilderFormData = {
  agentName: '',
  agentTagline: '',
  llmModel: '',
  personalityPrompt: 'You are a friendly and helpful assistant.',
  basicKnowledge: '',
  selectedToolSlugs: [],
  websiteDomain: '',
  notificationEmail: '',
};

const prevButtonClasses = 'text-slate-700 bg-white';
const nextButtonClasses = 'text-white bg-indigo-600';
const buttonBaseClasses =
  'px-6 py-2 text-sm font-medium border-2 border-black rounded-lg transition-all duration-150 ease-out';
const buttonInteractionClasses =
  'hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[4px_4px_0_0_#000] active:translate-x-0 active:translate-y-0 active:shadow-none';
const buttonDisabledClasses =
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:hover:translate-x-0 disabled:hover:translate-y-0';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
const DOMAIN_REGEX = /^([a-zA-Z0-9-_.]+)(:\d+)?$/;

type DomainErrorKey =
  | 'quickstart.validation.requiredDomain'
  | 'quickstart.validation.invalidDomain';

type DomainResult = { values: string[] } | { errorKey: DomainErrorKey };

type EmailErrorKey = 'quickstart.validation.invalidEmailList';
type EmailResult = { emails: string[] } | { errorKey: EmailErrorKey };

const normaliseDomains = (value: string): DomainResult => {
  const trimmed = value.trim();
  if (!trimmed) {
    return { errorKey: 'quickstart.validation.requiredDomain' };
  }

  const entries = trimmed
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean);

  const invalid = entries.find((entry) => !DOMAIN_REGEX.test(entry));
  if (invalid) {
    return { errorKey: 'quickstart.validation.invalidDomain' };
  }

  return { values: entries.map((entry) => entry.toLowerCase()) };
};

const parseEmailList = (value: string): EmailResult => {
  const trimmed = value.trim();
  if (!trimmed) return { emails: [] };

  const entries = Array.from(
    new Set(
      trimmed
        .split(/[,;]+/)
        .map((token) => token.trim())
        .filter(Boolean),
    ),
  );

  const invalid = entries.find((email) => !EMAIL_REGEX.test(email));
  if (invalid) {
    return { errorKey: 'quickstart.validation.invalidEmailList' };
  }

  return { emails: entries };
};

export const AgentBuilderPage: React.FC = () => {
  const { t } = useTranslation('agentBuilder');
  const navigate = useNavigate();

  const {
    availableModels,
    availableTools,
    isLoading: isDataLoading,
    error: dataError,
    createAgent,
    isCreating,
    createAgentQuickstart,
    isQuickStarting,
  } = useCreateAgent();

  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [formData, setFormData] =
    useState<AgentBuilderFormData>(initialFormData);
  const [quickstartResult, setQuickstartResult] =
    useState<QuickstartAgentResult | null>(null);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);

  const [websiteDomainError, setWebsiteDomainError] =
    useState<string | null>(null);
  const [notificationEmailError, setNotificationEmailError] =
    useState<string | null>(null);
  const [fieldSyncState, setFieldSyncState] = useState(() =>
    createInitialOnboardingAssistantFieldSyncState(),
  );

  const formDataRef = useRef(formData);
  const fieldSyncStateRef = useRef(fieldSyncState);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    fieldSyncStateRef.current = fieldSyncState;
  }, [fieldSyncState]);

  const handleFieldChange = useCallback(
    <K extends keyof AgentBuilderFormData>(
      field: K,
      value: AgentBuilderFormData[K],
    ) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleSyncedFieldChange = useCallback(
    <K extends OnboardingAssistantSyncField>(field: K, value: string) => {
      handleFieldChange(field, value as AgentBuilderFormData[K]);
      setFieldSyncState((prev) => ({
        ...prev,
        [field]: { source: 'manual' },
      }));
    },
    [handleFieldChange],
  );

  const handleToolToggle = useCallback(
    (toolSlug: string) => {
      const newSlugs = new Set(formData.selectedToolSlugs);
      if (newSlugs.has(toolSlug)) newSlugs.delete(toolSlug);
      else newSlugs.add(toolSlug);
      handleFieldChange('selectedToolSlugs', Array.from(newSlugs));
    },
    [formData.selectedToolSlugs, handleFieldChange],
  );

  const effectiveModelId = useMemo(() => {
    return formData.llmModel || availableModels?.[0]?.id || 'gpt-4o-mini';
  }, [formData.llmModel, availableModels]);

  const handleAssistantFieldPatch = useCallback(
    (
      patch: OnboardingAssistantFieldPatch,
      options?: { confirmOverwrite?: boolean },
    ): OnboardingAssistantSyncPatchResult => {
      const updates: Partial<AgentBuilderFormData> = {};
      const results: OnboardingAssistantSyncPatchResult['results'] = {};
      const appliedFields: OnboardingAssistantSyncField[] = [];
      const skippedFields: OnboardingAssistantSyncField[] = [];
      const confirmOverwrite = options?.confirmOverwrite === true;

      for (const [field, nextValue] of Object.entries(
        patch,
      ) as Array<[OnboardingAssistantSyncField, string | undefined]>) {
        if (typeof nextValue !== 'string') {
          continue;
        }

        const currentValue = String(formDataRef.current[field] ?? '');
        const currentSource = fieldSyncStateRef.current[field]?.source ?? 'initial';
        const isManualLock =
          currentSource === 'manual' &&
          currentValue.trim().length > 0 &&
          currentValue !== nextValue;

        if (isManualLock && !confirmOverwrite) {
          results[field] = {
            status: 'ignored',
            code: 'manual_override',
          };
          skippedFields.push(field);
          continue;
        }

        if (currentValue === nextValue) {
          results[field] = {
            status: 'ignored',
            code: 'unchanged',
          };
          skippedFields.push(field);
          continue;
        }

        updates[field] = nextValue as AgentBuilderFormData[typeof field];
        results[field] = { status: 'applied' };
        appliedFields.push(field);
      }

      if (appliedFields.length > 0) {
        setFormData((prev) => ({
          ...prev,
          ...updates,
        }));
        setFieldSyncState((prev) => {
          const nextState = { ...prev };
          for (const field of appliedFields) {
            nextState[field] = { source: 'assistant' };
          }
          return nextState;
        });
      }

      if (!appliedFields.length && !skippedFields.length) {
        return {
          status: 'error',
          appliedFields: [],
          skippedFields: [],
          results: {},
          message: 'No valid onboarding fields provided.',
          confirmOverwrite,
        };
      }

      return {
        status: 'success',
        appliedFields,
        skippedFields,
        results,
        confirmOverwrite,
      };
    },
    [],
  );

  const onboardingAssistantContext = useMemo(
    () =>
      buildOnboardingAssistantContext(formData, fieldSyncState, {
        activeStepId: STEPS[activeStepIndex]?.id ?? 'craft',
        effectiveModelId,
        availableModelIds: (availableModels ?? []).map((model) => model.id),
      }),
    [formData, fieldSyncState, activeStepIndex, effectiveModelId, availableModels],
  );

  const handleLegacyCreate = useCallback(
    async (combinedPromptContent: string) => {
      const payload: CreateAgentPayload = {
        name: formData.agentName,
        llmModel: effectiveModelId,
        description: formData.agentTagline,
        toolSlugs: formData.selectedToolSlugs,
        promptOption: {
          type: 'new',
          name: `${formData.agentName} - System Prompt`,
          content: combinedPromptContent,
        },
      };

      const newAgent = await createAgent(payload);
      toast.success(
        t('validation.creationSuccess', { agentName: newAgent.name }),
      );
      navigate(`/app/agents/${newAgent.id}`);
    },
    [
      createAgent,
      formData.agentName,
      formData.agentTagline,
      formData.selectedToolSlugs,
      effectiveModelId,
      navigate,
      t,
    ],
  );

  const handleSaveConfigSuccessNavigation = useCallback(() => {
    setIsResultModalOpen(false);
    if (quickstartResult?.agent_id) {
      navigate(`/app/agents/${quickstartResult.agent_id}`);
    }
  }, [navigate, quickstartResult]);

  const handleSaveConfigOpenChannels = useCallback(() => {
    setIsResultModalOpen(false);
    if (quickstartResult?.agent_id) {
      navigate(`/app/agents/${quickstartResult.agent_id}/channels`);
    } else {
      navigate('/app/agents');
    }
  }, [navigate, quickstartResult]);

  const handleSaveConfigCopy = useCallback(() => {
    if (quickstartResult?.embed_script) {
      void navigator.clipboard.writeText(quickstartResult.embed_script);
    }
  }, [quickstartResult]);

  const handleSaveAndActivate = async () => {
    if (!formData.agentName) {
      toast.error(
        t(
          'quickstart.validation.missingName',
          'Please provide an Agent Name.',
        ),
      );
      setActiveStepIndex(0);
      return;
    }

    const domainParse = normaliseDomains(formData.websiteDomain);
    if ('errorKey' in domainParse) {
      setWebsiteDomainError(t(domainParse.errorKey));
      setActiveStepIndex(0);
      return;
    }
    setWebsiteDomainError(null);

    const emailResult = parseEmailList(formData.notificationEmail);
    if ('errorKey' in emailResult) {
      setNotificationEmailError(t(emailResult.errorKey));
      setActiveStepIndex(0);
      return;
    }
    setNotificationEmailError(null);

    const sanitizedDomains = domainParse.values;
    if (sanitizedDomains.join(', ') !== formData.websiteDomain.trim()) {
      handleFieldChange('websiteDomain', sanitizedDomains.join(', '));
    }

    const emailString = emailResult.emails.join(', ');
    if (emailString !== formData.notificationEmail.trim()) {
      handleFieldChange('notificationEmail', emailString);
    }

    const combinedPromptContent = `# Personality
${formData.personalityPrompt}

# Knowledge
${formData.basicKnowledge}`.trim();

    try {
      const quickstartResponse = await createAgentQuickstart({
        agentName: formData.agentName,
        promptContent: combinedPromptContent,
        websiteDomain: sanitizedDomains[0] ?? '',
        widgetConfig: undefined,
        notifyEmails: emailResult.emails.length ? emailResult.emails : undefined,
      });

      if (sanitizedDomains.length > 0) {
        if (quickstartResponse?.installation_id) {
          const updateResp = await widgetInstallationsService.updateAllowedOrigins(
            quickstartResponse.installation_id as UUID,
            sanitizedDomains,
          );
          if (isApiError(updateResp)) {
            console.warn(
              '[builder] Failed updating installation allowed origins',
              updateResp.message,
            );
          }
        } else if (quickstartResponse?.widget_template_id) {
          const updateResp = await widgetService.updateTemplateAllowedOrigins(
            quickstartResponse.widget_template_id as UUID,
            sanitizedDomains,
          );
          if (isApiError(updateResp)) {
            console.warn(
              '[builder] Failed updating template allowed origins',
              updateResp.message,
            );
          }
        }
      }

      setQuickstartResult(quickstartResponse);
      setIsResultModalOpen(true);
    } catch (error: any) {
      const message =
        error?.message ||
        error?.data?.detail ||
        t('quickstart.validation.fallbackUnknown', 'Unknown error');
      toast.error(t('quickstart.validation.quickstartFailed', { message }));
      await handleLegacyCreate(combinedPromptContent);
    }
  };

  const handleNext = () =>
    setActiveStepIndex((prev) => Math.min(STEPS.length - 1, prev + 1));
  const handlePrev = () =>
    setActiveStepIndex((prev) => Math.max(0, prev - 1));

  if (isDataLoading) {
    return <div>{t('common.loading', 'Loading...')}</div>;
  }

  if (dataError) {
    return <div>{t('common.error', { message: dataError.message })}</div>;
  }

  const isSaving = isCreating || isQuickStarting;

  const renderCurrentStep = () => {
    switch (activeStepIndex) {
      case 0:
        return (
          <DefineAgentStep
            agentName={formData.agentName}
            onAgentNameChange={(value: string) =>
              handleSyncedFieldChange('agentName', value)
            }
            agentTagline={formData.agentTagline}
            onAgentTaglineChange={(value: string) =>
              handleSyncedFieldChange('agentTagline', value)
            }
            availableModels={availableModels || []}
            selectedModel={formData.llmModel || effectiveModelId}
            onModelChange={(value: string) =>
              handleFieldChange('llmModel', value)
            }
            websiteDomainsInput={formData.websiteDomain}
            onWebsiteDomainsInputChange={(value: string) =>
              handleSyncedFieldChange('websiteDomain', value)
            }
            websiteDomainsError={websiteDomainError}
            notificationEmail={formData.notificationEmail}
            onNotificationEmailChange={(value: string) =>
              handleSyncedFieldChange('notificationEmail', value)
            }
            notificationEmailError={notificationEmailError}
          />
        );
      case 1:
        return (
          <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(360px,0.9fr)]">
            <CraftMindStep
              personalityPrompt={formData.personalityPrompt}
              onPersonalityPromptChange={(value: string) =>
                handleSyncedFieldChange('personalityPrompt', value)
              }
              basicKnowledge={formData.basicKnowledge}
              onBasicKnowledgeChange={(value: string) =>
                handleSyncedFieldChange('basicKnowledge', value)
              }
            />
            <OnboardingAssistantPanel
              agentName={formData.agentName}
              assistantContext={onboardingAssistantContext}
              onApplyFieldPatch={handleAssistantFieldPatch}
            />
          </div>
        );
      case 2:
        return (
          <AgentAbilitiesStep
            availableTools={availableTools || []}
            selectedToolSlugs={formData.selectedToolSlugs}
            onToolToggle={handleToolToggle}
          />
        );
      default:
        return <div>{t('common.invalidStep', 'Invalid Step')}</div>;
    }
  };

  const leftControls = (
    <div className="flex gap-2">
      <Button
        onClick={handlePrev}
        disabled={activeStepIndex === 0}
        className={clsx(
          buttonBaseClasses,
          prevButtonClasses,
          buttonInteractionClasses,
          buttonDisabledClasses,
        )}
        text={t('actions.previous', 'Previous')}
      />
      {activeStepIndex < STEPS.length - 1 && (
        <Button
          onClick={handleNext}
          className={clsx(
            buttonBaseClasses,
            nextButtonClasses,
            buttonInteractionClasses,
            buttonDisabledClasses,
          )}
          text={t('actions.next', 'Next')}
        />
      )}
    </div>
  );

  const rightControls =
    activeStepIndex === STEPS.length - 1 ? (
      <Button
        onClick={handleSaveAndActivate}
        className={clsx(
          buttonBaseClasses,
          nextButtonClasses,
          buttonInteractionClasses,
          buttonDisabledClasses,
        )}
        disabled={isSaving}
        text={isSaving ? t('common.saving', 'Saving...') : t('actions.save', 'Save')}
      />
    ) : null;

  return (
    <>
      <div className="mx-auto flex max-w-5xl flex-col gap-8 px-6 py-8">
        <header className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-extrabold text-slate-900">
              {t('pageTitle', 'Agent Builder')}
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              {t('pageSubtitle', 'Create, configure, and activate your Dopomogai agent.')}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {leftControls}
            {rightControls}
          </div>
        </header>

        <AgentBuilderStepper
          steps={STEPS}
          currentStepIndex={activeStepIndex}
          onStepClick={setActiveStepIndex}
        />

        <div className="rounded-2xl border-2 border-black bg-white p-6 shadow-offset-black">
          {renderCurrentStep()}
        </div>
      </div>

      <Modal
        isOpen={isResultModalOpen}
        onClose={() => setIsResultModalOpen(false)}
        title={t('quickstart.modal.title', 'Agent ready!')}
        size="lg"
        footer={
          <div className="flex flex-wrap justify-end gap-3">
            <Button
              variant="secondary"
              text={t('quickstart.modal.copy', 'Copy embed')}
              onClick={handleSaveConfigCopy}
            />
            <Button
              variant="outline"
              text={t('quickstart.modal.openChannels', 'Open channels')}
              onClick={handleSaveConfigOpenChannels}
            />
            <Button
              variant="primary"
              text={t('quickstart.modal.openAgent', 'Open agent')}
              onClick={handleSaveConfigSuccessNavigation}
            />
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-slate-600">
            {t('quickstart.modal.description', 'Here’s your concierge embed code.')}
          </p>
          <CodeEditorField
            value={
              quickstartResult?.embed_script
                ? quickstartResult.embed_script
                : '// No embed script available.'
            }
            onChange={() => {}}
            language="html"
            readOnly
            height="200px"
          />
        </div>
      </Modal>
    </>
  );
};
